import { Box } from '@material-ui/core';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';

import { showModal } from '../../actions/modalActions';
import { ModalTypes } from '../../actions/types';
import { EventInfo } from '../../components/eventInfo/EventInfo';
import { EventSummary } from '../../components/eventSummary/EventSummary';
import { RaceDistances } from '../../components/raceDistances/RaceDistances';
import { Schedule } from '../../components/schedule/Schedule';
import { Courses } from '../../components/ELP/courses/Courses';
import { Locations } from '../../components/ELP/locations/Locations';
import { Sponsors } from '../../components/sponsors/Sponsors';
import { ELP } from '../../components/structuredData/ELP';
import { AddEventResultsButton } from '../../components/shared/AddEventResultsButton';
import { LogInOrSignUpIndependent } from '../../components/modal/LogInOrSignUpIndependent';
import { ElpResults } from '../../components/elpResults/ElpResults';
import { isLoggedIn } from '../../utils/isLoggedIn';
import { isTodayOrFuture } from '../../utils/date';
import { trackGoogleEvent } from '../../utils/googleEvents';
import { useExtra } from '../../hooks/useExtra';
import { MasterEventApiData } from '../../hooks/useMasterEvent';

import { useStyles } from './styles';

const mapStateToProps = () => ({
  dispatch: undefined
});

type EventAboutPageComponentProps = {
  dispatch?: any;
  isMobile: boolean;
  masterEvent: MasterEventApiData;
  eventsObj: any;
};

const EventAboutPageComponent = ({
  dispatch,
  isMobile,
  masterEvent,
  eventsObj,
}: EventAboutPageComponentProps) => {
  const { t } = useTranslation();
  const styles = useStyles();

  const [loginOpen, setLoginOpen] = useState(false);

  const { extra } = useExtra(masterEvent.masterEventID, masterEvent.currentRace.athlinksId);

  const isDifferentEvent = (eventsObj.current !== eventsObj.next);

  const showScheduleOrDistances = masterEvent && isDifferentEvent;

  const addEventResultsModal = useCallback(() => {
    dispatch(showModal({
      modalProps: {
        currentPage: 0,
        event: {...masterEvent, country: masterEvent.countryName, state: masterEvent.stateProvAbbrev},
        isMobile,
        noScroll: true
      },
      modalType: ModalTypes.ADD_EVENT_WIZARD,
      pages: 2
    }))
  }, [dispatch, isMobile, masterEvent])

  const onClickAddMissingResults = useCallback(() => {
    trackGoogleEvent('ELP', 'Click', 'addMissingResults');
    isLoggedIn()
      ? addEventResultsModal()
      : setLoginOpen(true);
  }, [addEventResultsModal]);

  const resultsSection = useMemo(() => (
    <>
      <ElpResults
        master={masterEvent}
        isMobile={isMobile}
      />

      <AddEventResultsButton callback={onClickAddMissingResults}
      />
    </>
  ), [isMobile, masterEvent, onClickAddMissingResults])

  const coursesSection = useMemo(() => {
    if (isDifferentEvent) {
      return <Courses courseMaps={extra?.courseMaps || []} />;
    } 

    return null;
  }, [extra, isDifferentEvent])

  return (
    <div>
      <ELP
        event={masterEvent.currentRace}
        sponsors={extra?.sponsors || []}
      />

      <div className={styles.aboutPageContent}>
        <Box flex={1}>
          <LogInOrSignUpIndependent
            firstSlideType='claim-results'
            closeAction={() => setLoginOpen(false)}
            isMobile={isMobile}
            open={loginOpen}
          />

          {isDifferentEvent &&
            <div className={styles.aboutPageSummaryWrapper}>
              <EventSummary event={masterEvent.currentRace} />
            </div>
          }

          <div className={styles.aboutPageDistancesSection}>
            <div className={styles.aboutPageDistancesSectionLeftSide}>
              {showScheduleOrDistances && (
                <>
                  {isTodayOrFuture(masterEvent.currentRace.startTime) ? (
                    <Schedule event={masterEvent.currentRace} schedule={extra?.schedule} />
                  ) : (
                    <RaceDistances event={masterEvent.currentRace} t={t} />
                  )}
                </>
              )}

              <Locations locations={extra?.extraLocations} />
            </div>

            <div>
              <EventInfo event={masterEvent.currentRace} />
            </div>
          </div>
        </Box>

        <div className={styles.aboutPageDesktopSection}>
          {resultsSection}
        </div>
      </div>

      <div className={styles.aboutPageMobileSection}>
        {coursesSection}
      </div>

      <div className={styles.aboutPageMobileSection}>
        {resultsSection}
      </div>

      <div className={`${styles.aboutPageDesktopSection} ${styles.aboutPageCourseSectionWrapper}`}>
        {coursesSection}
      </div>

      <Sponsors logos={extra?.sponsors || []} />
    </div>
  );
};

export const EventAboutPage = connect(mapStateToProps)(EventAboutPageComponent);
